import {
  Linking,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import React, { FC, useCallback, useMemo } from 'react';
import { ReciprocalClub } from '../types';
import { STYLE_VARS } from '../../../constants/style-vars';
import { Image } from '../../../components/Image';
import { IS_WEB } from '../../../constants/platform';
import { addBase64Prefix } from '../../../services/addBase64Prefix';
import {
  address_white,
  email_white,
  fax_white,
  group_people,
  network_icon,
  open_board,
  phone_white,
} from '../../../assets/base64';
import { useIsDesktop } from '../../../hooks/useIsDeskTop';
import { COLORS, FONTS } from '../../../constants';
import { IClub } from '../../../types/services.types';

interface ReciprocalClubItemProps {
  item: IClub;
}

const ReciprocalClubItem: FC<ReciprocalClubItemProps> = ({ item }) => {
  const isDesktop = useIsDesktop();

  const handleOpenGGMap = useCallback(
    (ggMap: { lat: number; lng: number; name: string; zoom: number }) => {
      const scheme = `https://maps.google.com/maps/search/${ggMap.name}/@`;
      const latLng = `${ggMap.lat},${ggMap.lng},${ggMap.zoom}z`;
      const url = `${scheme}${latLng}`;

      if (url) {
        Linking.openURL(url);
      }
    },
    []
  );

  return (
    <View
      style={[
        styles.wrapper,
        {
          flexDirection: isDesktop() ? 'row' : undefined,
          justifyContent: isDesktop() ? 'space-between' : undefined,
        },
      ]}
    >
      <View style={{ width: isDesktop() ? '60%' : undefined }}>
        <Text style={styles.titleStyle}>{item.name}</Text>
        <View
          style={{
            marginVertical: 15,
            gap: 7,
          }}
        >
          <View style={styles.row}>
            <Image
              source={
                IS_WEB
                  ? addBase64Prefix(address_white)
                  : require('../../../assets/address-white.png')
              }
              style={styles.iconStyle}
            />
            <Text style={styles.nomalText}>{item.location}</Text>
          </View>
          <TouchableOpacity
            style={styles.row}
            onPress={() => {
              if (item.google_maps) {
                handleOpenGGMap(item.google_maps);
              }
            }}
          >
            <Image
              source={
                IS_WEB
                  ? addBase64Prefix(address_white)
                  : require('../../../assets/address-white.png')
              }
              style={styles.iconStyle}
            />
            <Text style={[styles.nomalText, { fontWeight: 'bold' }]}>
              View Map
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.row}
            onPress={() => {
              Linking.openURL(item.website);
            }}
          >
            <Image
              source={
                IS_WEB
                  ? addBase64Prefix(network_icon)
                  : require('../../../assets/network-icon.png')
              }
              style={styles.iconStyle}
            />
            <Text style={styles.nomalText}>{item.website}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.row}
            onPress={() => {
              Linking.openURL(`mailto:${item.email}`);
            }}
          >
            <Image
              source={
                IS_WEB
                  ? addBase64Prefix(email_white)
                  : require('../../../assets/email-white.png')
              }
              style={styles.iconStyle}
            />
            <Text style={styles.nomalText}>{item.email}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.row}
            onPress={() => {
              Linking.openURL(`tel:${item.phone}`);
            }}
          >
            <Image
              source={
                IS_WEB
                  ? addBase64Prefix(phone_white)
                  : require('../../../assets/phone-white.png')
              }
              style={styles.iconStyle}
            />
            <Text style={styles.nomalText}>{item.phone}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.row}
            onPress={() => {
              Linking.openURL(`tel:${item.fax}`);
            }}
          >
            <Image
              source={
                IS_WEB
                  ? addBase64Prefix(fax_white)
                  : require('../../../assets/fax-white.png')
              }
              style={styles.iconStyle}
            />
            <Text style={styles.nomalText}>{item.fax}</Text>
          </TouchableOpacity>
        </View>
      </View>
      <View style={{ width: isDesktop() ? '35%' : undefined }}>
        <View style={{ marginBottom: 7 }}>
          <Text style={styles.titleStyle}>Facilities Available</Text>
          <View
            style={{ marginTop: 10, flexDirection: 'row', flexWrap: 'wrap' }}
          >
            {item.facilities?.map((facility, index) => (
              <View
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 24,
                  borderWidth: 1,
                  borderColor: STYLE_VARS.primaryColor,
                  backgroundColor: 'white',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 10,
                  marginBottom: 3,
                }}
                key={facility.id}
              >
                <Image
                  source={{ uri: facility.icon }}
                  style={{
                    width: 16,
                    height: 16,
                    resizeMode: 'contain',
                    tintColor: STYLE_VARS.primaryColor,
                  }}
                />
              </View>
            ))}
          </View>
        </View>
        {item.people && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginBottom: 7,
            }}
          >
            <Image
              source={
                IS_WEB
                  ? addBase64Prefix(group_people)
                  : require('../../../assets/group-people.png')
              }
              style={{
                width: 23,
                height: 18,
                marginRight: 5,
              }}
            />
            <Text
              style={{
                fontFamily: FONTS.AvenirMedium,
                fontSize: 12,
                color: COLORS.DARKER_GREY,
              }}
            >{`MAX: ${item.people}`}</Text>
          </View>
        )}
        {item.open_time && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Image
              source={
                IS_WEB
                  ? addBase64Prefix(open_board)
                  : require('../../../assets/open-board.png')
              }
              style={{
                width: 23,
                height: 18,
                marginRight: 5,
              }}
            />
            <Text
              style={{
                fontFamily: FONTS.AvenirMedium,
                fontSize: 12,
                color: COLORS.DARKER_GREY,
                textTransform: 'uppercase',
              }}
            >
              {item.open_time}
            </Text>
          </View>
        )}
      </View>
    </View>
  );
};

export default ReciprocalClubItem;

const styles = StyleSheet.create({
  wrapper: {
    padding: 10,
    backgroundColor: 'white',
    borderColor: STYLE_VARS.lightGreen,
    borderRadius: 4,
    borderWidth: 2,
    marginBottom: 25,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 0,
    },
    shadowOpacity: 0.3,
    shadowRadius: 13.5,
    elevation: 6,
  },
  iconStyle: {
    width: 12,
    marginTop: 3,
    height: 12,
    marginRight: 5,
    resizeMode: 'contain',
    tintColor: STYLE_VARS.primaryColor,
  },
  titleStyle: {
    fontFamily: FONTS.IowanOldStyleBTRoman,
    color: STYLE_VARS.textColor,
    fontSize: 15,
    fontWeight: '600',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    alignSelf: 'flex-start',
  },
  nomalText: {
    fontFamily: FONTS.AvenirMedium,
    color: STYLE_VARS.textColor,
    fontSize: 12,
    fontWeight: '500',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
});
